import { useEffect, useState } from "react";
import { Button, Chip, IconButton, MenuItem, Pagination, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip } from "@mui/material";
import Box from "elements/Box";
import AlertDialog from "elements/AlertDialog";
import AddIcon from "@mui/icons-material/Add";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import CategoryIcon from "@mui/icons-material/Category";
import { useSnackbar } from "notistack";
import { ApiRequest } from "GlobalFunctions";
import FaqEditor from "./FaqEditor";
import FaqCategories from "./FaqCategories";

export default function FaqList(props: any) {
  const { setLoading } = props;
  const { enqueueSnackbar } = useSnackbar();

  const [list, setList] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [categories, setCategories] = useState<any[]>([]);
  const [filterCategory, setFilterCategory] = useState("");

  const [openEditor, setOpenEditor] = useState(false);
  const [activeItem, setActiveItem] = useState<any>(null);
  const [openAlert, setOpenAlert] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [openCategories, setOpenCategories] = useState(false);

  useEffect(() => { setLoading(false); loadCategories(); }, []);
  useEffect(() => { loadList(); }, [page, filterCategory]);

  const loadList = () => {
    setLoading(true);
    const query: any = { page };
    if (filterCategory) query.faq_category_id = filterCategory;

    ApiRequest({
      url: "/faq/list",
      method: "get",
      headers: { ltkn: localStorage.getItem("ltkn") },
      query,
      setResponse: (response: any) => {
        setList(response.list || []);
        setTotalPages(response.total_pages || 0);
        setLoading(false);
      },
    });
  };

  const loadCategories = () => {
    ApiRequest({
      url: "/faq/categories/list",
      method: "get",
      headers: { ltkn: localStorage.getItem("ltkn") },
      query: { full_list: true },
      setResponse: (response: any) => {
        setCategories(response.list || []);
      },
    });
  };

  const handleDelete = () => {
    if (!deleteId) return;
    const body = new FormData();
    body.append("faq_item_id", String(deleteId));

    ApiRequest({
      url: "/faq/delete",
      method: "post",
      headers: { ltkn: localStorage.getItem("ltkn") },
      body,
      setResponse: (response: any) => {
        if (response.status === "success") {
          enqueueSnackbar("Pregunta eliminada", { variant: "success" });
          loadList();
        }
      },
    });
  };

  return (
    <div id="FaqList" className="ModuleContainer">
      <div className="ModuleWrapper">
        <div className="boxContainerHeader">
          <div className="boxContainerTitle">
            <h2 className="h2_title">Preguntas Frecuentes</h2>
            <span className="h2_subtitle">Gestiona las preguntas y respuestas</span>
          </div>
          <div className="boxContainerActions">
            <TextField
              select
              size="small"
              value={filterCategory}
              onChange={(e) => { setFilterCategory(e.target.value); setPage(1); }}
              sx={{ minWidth: 150 }}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="">Todas las categorias</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat.faq_category_id} value={cat.faq_category_id}>{cat.name}</MenuItem>
              ))}
            </TextField>
            <Button
              variant="outlined"
              startIcon={<CategoryIcon />}
              onClick={() => setOpenCategories(true)}
            >
              Categorias
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => { setActiveItem(null); setOpenEditor(true); }}
            >
              Nueva pregunta
            </Button>
          </div>
        </div>

        <Box className="box100">
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: "#a8a8a8", borderBottom: "none", width: 60 }}>Orden</TableCell>
                  <TableCell sx={{ color: "#a8a8a8", borderBottom: "none" }}>Pregunta</TableCell>
                  <TableCell sx={{ color: "#a8a8a8", borderBottom: "none", width: 140 }}>Categoria</TableCell>
                  <TableCell sx={{ color: "#a8a8a8", borderBottom: "none", width: 90 }}>Estado</TableCell>
                  <TableCell sx={{ color: "#a8a8a8", borderBottom: "none", width: 90 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.map((item) => (
                  <TableRow key={item.faq_item_id} hover>
                    <TableCell>{item.sort_order}</TableCell>
                    <TableCell>{item.question}</TableCell>
                    <TableCell>
                      <Chip label={item.category_name || "—"} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.active ? "Activo" : "Inactivo"}
                        color={item.active ? "success" : "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => { setActiveItem(item.faq_item_id); setOpenEditor(true); }}>
                          <ModeEditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton size="small" color="error" onClick={() => { setDeleteId(item.faq_item_id); setOpenAlert(true); }}>
                          <DeleteOutlineOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {list.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: "#999" }}>
                      No hay preguntas frecuentes
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, v) => setPage(v)}
              color="primary"
              style={{ marginTop: 20, alignSelf: "center" }}
            />
          )}
        </Box>
      </div>

      <AlertDialog
        open={openAlert}
        setOpen={setOpenAlert}
        subtitle="Se eliminara esta pregunta permanentemente."
        send={handleDelete}
      />

      <FaqEditor
        openEditor={openEditor}
        setOpenEditor={setOpenEditor}
        faq_item_id={activeItem}
        loadList={loadList}
        setLoading={setLoading}
        categories={categories}
      />

      <FaqCategories
        open={openCategories}
        setOpen={setOpenCategories}
        setLoading={setLoading}
        loadCategories={loadCategories}
      />
    </div>
  );
}
